import type { ScaleTime } from "d3-scale";
import { scaleTime } from "d3-scale";
import { timeTickInterval } from "d3-time";
import { type Ref, useImperativeHandle, useMemo, useState } from "react";

/** Handle the camera chart pushes the live ms viewport through each gesture frame. */
export interface LiveTimePreviewHandle {
  setPreview(domain: [number, number] | null): void;
}

const isValidDate = (d: Date): boolean => d instanceof Date && !Number.isNaN(d.getTime());

/**
 * Extra pixels of calendar ticks generated beyond each plot edge. Consumers clip
 * to the plot bounds, so these overscan ticks slide smoothly through the edge
 * (revealed/hidden by the clip) instead of being culled the instant their time
 * crosses the visible domain boundary — which made edge labels pop in/out.
 */
const OVERSCAN_PX = 160;

/**
 * Shared live-tick source for the camera chart's time axis and its vertical
 * gridlines. During a pan/zoom gesture the parent pushes the live ms domain via
 * the exposed `setPreview`; ticks are calendar-aligned ("nice") and recomputed
 * against the live (or committed) domain. Driving both the axis labels and the
 * gridlines from this one hook keeps them on identical positions.
 *
 * Ticks are overscanned a fixed pixel margin past both plot edges (same nice
 * interval, extrapolated positions via the unclamped active scale) so panning
 * labels enter and exit by clipping at the plot boundary rather than popping.
 */
export function useLiveTimeTicks(scale: ScaleTime<number, number>, width: number, ref: Ref<LiveTimePreviewHandle>) {
  const [preview, setPreview] = useState<[number, number] | null>(null);
  useImperativeHandle(ref, () => ({ setPreview }), []);

  const [rangeStart, rangeEnd] = scale.range();
  const activeScale = useMemo(
    () =>
      preview
        ? scaleTime()
            .domain([new Date(preview[0]), new Date(preview[1])])
            .range([rangeStart, rangeEnd])
        : scale,
    [preview, scale, rangeStart, rangeEnd]
  );

  const domain = activeScale.domain();
  const start = domain[0];
  const end = domain[domain.length - 1];
  const valid = isValidDate(start) && isValidDate(end);
  const compact = width < 400;
  const targetCount = Math.max(2, Math.min(8, Math.floor(width / (compact ? 70 : 110))));
  const ticks = useMemo(() => {
    if (!valid) return [];
    const pxSpan = rangeEnd - rangeStart;
    if (pxSpan <= 0) return activeScale.ticks(targetCount);

    const [d0, d1] = activeScale.domain();
    const startMs = d0.getTime();
    const endMs = d1.getTime();
    const msPerPx = (endMs - startMs) / pxSpan;
    const overscanPx = Math.min(OVERSCAN_PX, pxSpan);
    const overscanMs = overscanPx * msPerPx;

    // Enumerate ticks across the overscanned domain. Positions are read back
    // through `activeScale`, which is unclamped and so extrapolates the outer
    // ticks to their true past-the-edge pixels.
    const overscanScale = scaleTime()
      .domain([new Date(startMs - overscanMs), new Date(endMs + overscanMs)])
      .range([rangeStart - overscanPx, rangeEnd + overscanPx]);

    // Pin the tick interval to the one d3 would pick for the *visible* domain, so
    // the overscanned set is a strict superset of the visible ticks. Deriving the
    // count from the widened span instead can select a coarser/finer interval
    // (e.g. week vs 2-day), which would swap out which ticks render.
    const interval = timeTickInterval(d0, d1, targetCount);
    if (interval) {
      return overscanScale.ticks(interval);
    }
    // Degenerate/tiny range: timeTickInterval returns null — fall back to a
    // count derived from the widened span.
    const overscanCount = Math.max(2, Math.round((targetCount * (pxSpan + 2 * overscanPx)) / pxSpan));
    return overscanScale.ticks(overscanCount);
  }, [activeScale, targetCount, valid, rangeStart, rangeEnd]);

  return { activeScale, ticks, start, end, valid, compact, rangeStart, rangeEnd };
}
